<template>
  <div
    class="group relative w-full rounded-sm border border-l-4 bg-white px-4 py-6"
    :class="borderClass"
    draggable="true"
    data-cy="contract-role"
    @dragstart="startDrag"
    @dragend="$emit('dragend')"
  >
    <div class="flex w-full items-center space-x-6 gap-y-4">
      <div class="flex w-3/4 items-center space-x-6">
        <div>
          <icon-base
            :icon="IconDragHandle"
            width="7"
            height="12"
            viewBox="0 0 12 20"
            class="cursor-grab"
          />
        </div>
        <div class="flex w-full items-center space-x-6">
          <div>{{ index + 1 }}</div>
          <lf-dropdown
            :value="role.role"
            placeholder="Role"
            :name="`selected_role_${index}`"
            :options="options"
            full-width
            data-cy="role-dropdown"
            class="select-role"
            @change="emit('role:change', { role: $event, id: role.id })"
          >
            <template #type>
              <span
                class="ml-2 rounded-4 bg-gray-200 px-2 text-xxs font-semibold uppercase"
              >
                {{ getCountOfRole(role, activeRoles) }}
              </span>
            </template>
          </lf-dropdown>
        </div>
      </div>
      <div class="w-1/4">
        <div class="flex items-center justify-end space-x-6">
          <div
            v-if="hasEmptyFields"
            v-tooltip="$t('CONTRACTS.CONTRACT_BUILDER.MISSING_FIELDS')"
          >
            <icon-base :icon="IconWarning" class="text-warning" />
          </div>
          <button
            class="flex h-10 w-10 items-center justify-center rounded-sm border hover:text-red-500"
            data-cy="delete-role"
            @click.prevent="emit('role:delete')"
          >
            <icon-base :icon="IconTrashCanOutline" class="cursor-pointer" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import { storeToRefs } from "pinia";

import { getCountOfRole } from "@/helpers/contractBuilder";
import { useContractBuilderStore } from "@/stores/contractBuilder";

import IconDragHandle from "@/components/icons/IconDragHandle.vue";
import IconWarning from "@/components/icons/IconWarning.vue";
import IconTrashCanOutline from "@/components/icons/IconTrashCanOutline.vue";

import type { RoleWithID } from "@/models/contractBuilder";

const emit = defineEmits<{
  dragstart: [DragEvent];
  dragend: [];
  "role:change": [RoleWithID];
  "role:delete": [];
}>();

const props = defineProps<{
  role: RoleWithID;
  options: Record<string, string>;
  activeRoles: RoleWithID[];
  index: number;
}>();

const store = useContractBuilderStore();
const { activeTemplate } = storeToRefs(store);

const startDrag = (e: DragEvent) => {
  e.dataTransfer?.setData(
    "text",
    JSON.stringify({ role: props.role, index: props.index })
  );
  setTimeout(() => {
    emit("dragstart", e);
  }, 10);
};

const STYLES_FOR_ROLES = {
  signer: [
    "border-l-secondary-purple-1/50",
    "border-l-secondary-yellow/50",
    "border-l-flush-orange/50",
    "border-l-error/50",
  ],
  client: [
    "border-l-success/50",
    "border-l-stone/50",
    "border-l-green-700/50",
    "border-l-sky-900/50",
  ],
  guarantor: [
    "border-l-secondary-blue-4/50",
    "border-l-lime-400/50",
    "border-l-fuchsia-300/50",
    "border-l-amber-800/50",
  ],
};

const borderClass = computed(() => {
  const indexOfRole = getCountOfRole(props.role, props.activeRoles) || 1;
  switch (props.role.role) {
    case "signer":
      return (
        STYLES_FOR_ROLES.signer[indexOfRole - 1] ?? STYLES_FOR_ROLES.signer[0]
      );
    case "client":
      return (
        STYLES_FOR_ROLES.client[indexOfRole - 1] ?? STYLES_FOR_ROLES.client[0]
      );
    case "guarantor":
      return (
        STYLES_FOR_ROLES.guarantor[indexOfRole - 1] ??
        STYLES_FOR_ROLES.guarantor[0]
      );
    default:
      return "border-l-gray-300";
  }
});

const hasEmptyFields = computed(() => {
  const recipient = activeTemplate.value?.content?.recipients?.find(
    (recipient) => recipient.id === props.role.id
  );
  return !recipient?.fields?.length;
});
</script>
